/**
 * Plans and limits - the one place the frontend reads plan numbers from.
 *
 * The numbers are owned by the backend (system settings, admin-editable) and arrive
 * through the pricing API. PLAN_FALLBACK mirrors the backend defaults and is only used
 * while that request is loading or if it fails, so no page needs to hard-code a limit.
 */

import { annualSavingPercent } from './planMath';
import { fetchPricingTiers, type Currency, type PricingResponse, type TierLimits } from './payment';

/** The one name for the daily AI allowance, everywhere in the app. */
export const AI_QUESTIONS = 'AI questions';

/** A plan's limits. -1 means unlimited. */
export interface PlanLimits {
  /** Calendar-month allowance (free: 30). -1 = no monthly limit. */
  aiQuestionsPerMonth: number;
  /** Daily limit. -1 = no daily limit (the default for both plans). */
  aiQuestionsPerDay: number;
  activeBattles: number;
  rosters: number;
  collectionUnits: number;
  journalPhotos: number;
}

export interface PlanPrices {
  /** Symbol for the currency the prices are shown in (always GBP: checkout charges GBP). */
  currencySymbol: string;
  monthly: number;
  annual: number;
  /** What the yearly plan works out to per month. */
  annualPerMonth: number;
  /** Saving of yearly against twelve monthly payments, in whole percent (0 = none). */
  annualSavingPercent: number;
  /** Founding Member price per year and places left; null while the offer is not open. */
  founding: { price: number; seatsLeft: number } | null;
  /** Premium welcome week for new accounts, in days (0 = off). */
  welcomeWeekDays: number;
  /** The one-line "fair use" explanation. */
  fairUseNote: string;
}

export interface Plans {
  free: PlanLimits;
  premium: PlanLimits;
  prices: PlanPrices;
}

/** Mirrors backend system_settings_service quota and pricing defaults. */
export const PLAN_FALLBACK: Plans = {
  free: { aiQuestionsPerMonth: 30, aiQuestionsPerDay: -1, activeBattles: 3, rosters: 25, collectionUnits: 1000, journalPhotos: 250 },
  premium: { aiQuestionsPerMonth: -1, aiQuestionsPerDay: -1, activeBattles: -1, rosters: -1, collectionUnits: -1, journalPhotos: -1 },
  prices: {
    currencySymbol: '£',
    monthly: 4.99,
    annual: 39,
    annualPerMonth: 3.25,
    annualSavingPercent: 35,
    founding: null,
    welcomeWeekDays: 7,
    fairUseNote: "Fair use applies: it keeps one account from running up huge costs. Normal players won't notice it.",
  },
};

/** "Unlimited" for -1, otherwise the number. */
export function formatLimit(value: number): string {
  return value === -1 ? 'Unlimited' : String(value);
}

/** The allowance in short form: "30 a month", "20 a day" or "Unlimited" (a monthly limit wins when both are set). */
export function aiAllowanceShort(limits: Pick<PlanLimits, 'aiQuestionsPerMonth' | 'aiQuestionsPerDay'>): string {
  if (limits.aiQuestionsPerMonth !== -1) return `${limits.aiQuestionsPerMonth} a month`;
  if (limits.aiQuestionsPerDay !== -1) return `${limits.aiQuestionsPerDay} a day`;
  return 'Unlimited';
}

/** "30 AI questions a month" / "Unlimited AI questions". */
export function aiAllowance(limits: Pick<PlanLimits, 'aiQuestionsPerMonth' | 'aiQuestionsPerDay'>): string {
  const short = aiAllowanceShort(limits);
  return short === 'Unlimited' ? `Unlimited ${AI_QUESTIONS}` : short.replace(/^(\d+)/, `$1 ${AI_QUESTIONS}`);
}

const toLimits = (limits: TierLimits | null | undefined, fallback: PlanLimits): PlanLimits =>
  limits
    ? {
        aiQuestionsPerMonth: limits.queries_per_month ?? fallback.aiQuestionsPerMonth,
        aiQuestionsPerDay: limits.queries_per_day,
        activeBattles: limits.max_active_battles,
        rosters: limits.max_armies,
        collectionUnits: limits.max_collection_items ?? fallback.collectionUnits,
        journalPhotos: limits.max_journal_photos ?? fallback.journalPhotos,
      }
    : fallback;

/** Build the plan table from a pricing response. */
export function plansFromPricing(pricing: PricingResponse): Plans {
  const free = pricing.tiers.find((t) => t.id === 'free');
  const monthly = pricing.tiers.find((t) => t.id === 'monthly');
  const annual = pricing.tiers.find((t) => t.id === 'annual');
  const founding = pricing.tiers.find((t) => t.id === 'founding');
  const monthlyPrice = monthly?.price_gbp ?? PLAN_FALLBACK.prices.monthly;
  const annualPrice = annual?.price_gbp ?? PLAN_FALLBACK.prices.annual;
  return {
    free: toLimits(free?.limits, PLAN_FALLBACK.free),
    premium: toLimits(monthly?.limits ?? annual?.limits, PLAN_FALLBACK.premium),
    prices: {
      currencySymbol: '£',
      monthly: monthlyPrice,
      annual: annualPrice,
      annualPerMonth: annual?.monthly_equivalent_gbp ?? Math.round((annualPrice / 12) * 100) / 100,
      annualSavingPercent: annual?.saving_percent ?? annualSavingPercent(monthlyPrice, annualPrice),
      founding:
        founding && (founding.seats_remaining ?? 0) > 0
          ? { price: founding.price_gbp, seatsLeft: founding.seats_remaining ?? 0 }
          : null,
      welcomeWeekDays: pricing.welcome_week_days ?? PLAN_FALLBACK.prices.welcomeWeekDays,
      fairUseNote: pricing.fair_use_note || PLAN_FALLBACK.prices.fairUseNote,
    },
  };
}

let cached: Promise<Plans> | null = null;

/** Fetch the plan table once per page load (shared by every caller). */
export function fetchPlans(currency?: Currency): Promise<Plans> {
  if (currency) return fetchPricingTiers(currency).then(plansFromPricing);
  if (!cached) {
    cached = fetchPricingTiers()
      .then(plansFromPricing)
      .catch(() => {
        cached = null;
        return PLAN_FALLBACK;
      });
  }
  return cached;
}

/** Forget the cached plan table (tests). */
export function resetPlansCache(): void {
  cached = null;
}
